import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import type { TaxonomyOption } from './TaxonomySelect';
import './ContentReview.css';

/**
 * WS2 (D75) — admin surface for the taxonomy registry. Lists Type +
 * Channel options (including inactive), with rename / activate-deactivate
 * and, for channels, re-mapping the postApprovalAction. User-added
 * channels arrive mapped to 'manual' and carry an "unmapped" hint until
 * an admin routes them.
 */

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const ALL_FORMATS = ['pdf', 'docx', 'pptx', 'image', 'video', 'webpage'];

const ACCESS_LABELS: Record<string, string> = {
  PUBLIC_INDEXABLE: 'Public (indexable)',
  PUBLIC_NOT_INDEXABLE: 'Public (unlisted)',
  PRIVATE: 'Private (login required)'
};

const ACTION_LABELS: Record<string, string> = {
  content_hub: 'Content Hub upload (automated)',
  website_page: 'Web page publish (automated)',
  portal_content: 'Portal upload + HubDB (automated)',
  manual: 'Notify only (manual)'
};

interface TaxonomyAdminProps {
  getAuthHeaders: () => Promise<Record<string, string>>;
  // Fredrik 2026-08-14 — folder-popup items open their cycle directly.
  onOpenCycle?: (cycleId: string) => void;
}

const TaxonomyAdmin: React.FC<TaxonomyAdminProps> = ({ getAuthHeaders, onOpenCycle }) => {
  const [options, setOptions] = useState<TaxonomyOption[]>([]);
  // Fredrik 2026-08-14 — live composed folder overview + rename impact.
  type StructRow = { path: string[]; count: number; items?: Array<{ _id?: string; name?: string; version?: string }> };
  const [structure, setStructure] = useState<{ approved: StructRow[]; archive: StructRow[]; working: StructRow[] } | null>(null);
  const [hoveredFolder, setHoveredFolder] = useState<string | null>(null);
  const [folderModal, setFolderModal] = useState<{ title: string; isLeaf: boolean; count: number; items: Array<{ _id?: string; name?: string; version?: string }> } | null>(null);
  const [usage, setUsage] = useState<{ cycles: number; archives: number; labelStored: boolean } | null>(null);
  // Fredrik 2026-08-14 — add options directly from the Registries (was
  // only possible inline in the submit form's dropdowns).
  const [addingKind, setAddingKind] = useState<string | null>(null);
  const [addLabel, setAddLabel] = useState('');
  const [addParent, setAddParent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get<TaxonomyOption[]>(
        `${backendUrl}/api/content-review/taxonomy?includeInactive=1`, { headers }
      );
      setOptions(res.data || []);
      try {
        const structRes = await axios.get(`${backendUrl}/api/content-review/folder-structure`, { headers });
        setStructure(structRes.data || null);
      } catch { setStructure(null); }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load taxonomy');
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Rename-impact lookup — fired when a label edit opens. For the
  // label-stored dimensions (type/audience/channel) the count is the
  // blast radius: existing items keep the OLD label until
  // rename-propagation ships. For slug-stored ones it is informational
  // (folders are named by slugs; a relabel moves nothing).
  async function fetchUsage(id: string) {
    setUsage(null);
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get(`${backendUrl}/api/content-review/taxonomy/${id}/usage`, { headers });
      setUsage(res.data || null);
    } catch { /* informational only */ }
  }

  async function addOption(kind: string) {
    const label = addLabel.trim();
    if (label.length < 2) return;
    try {
      setBusy(true);
      const headers = await getAuthHeaders();
      await axios.post(`${backendUrl}/api/content-review/taxonomy`,
        { kind, label, ...(addParent ? { parent: addParent } : {}) }, { headers });
      setAddingKind(null);
      setAddLabel('');
      setAddParent('');
      await fetchAll();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add option');
    } finally {
      setBusy(false);
    }
  }

  async function patch(id: string, body: Record<string, unknown>) {
    try {
      setBusy(true);
      const headers = await getAuthHeaders();
      await axios.patch(`${backendUrl}/api/content-review/taxonomy/${id}`, body, { headers });
      setEditingId(null);
      await fetchAll();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Update failed');
    } finally {
      setBusy(false);
    }
  }

  // F7 — make "all-checked because the list is empty" visually distinct
  // from an explicitly pinned full list.
  function allFormatsBadge(o: { allowedFormats?: string[] }) {
    if ((o.allowedFormats || []).length !== 0) return null;
    return (
      <span style={{ fontSize: 10, color: '#888', border: '1px solid #dee2e6', borderRadius: 8, padding: '1px 6px', marginLeft: 4 }}
        title="No restriction stored — this channel accepts every format. Uncheck one to pin an explicit list.">
        all (unrestricted)
      </span>
    );
  }

  // Fredrik 2026-08-14 (round 2) — render the composed paths as a real
  // indented tree (the flat path list was unreadable as an overview).
  type PathTree = { children: Record<string, PathTree>; count: number; items: Array<{ _id?: string; name?: string; version?: string }> };
  function buildPathTree(rows: StructRow[]): PathTree {
    const root: PathTree = { children: {}, count: 0, items: [] };
    for (const row of rows) {
      root.count += row.count;
      let cur = root;
      for (const seg of row.path) {
        if (!cur.children[seg]) cur.children[seg] = { children: {}, count: 0, items: [] };
        cur = cur.children[seg];
        cur.count += row.count;
        // Items roll up every level, capped so a busy branch stays light.
        if (row.items && cur.items.length < 30) {
          cur.items.push(...row.items.slice(0, 30 - cur.items.length));
        }
      }
    }
    return root;
  }

  // slug → display label + kind across kinds (year segments pass through).
  const slugLabel: Record<string, string> = {};
  const slugKind: Record<string, string> = {};
  for (const o of options) {
    const sl = (o as any).slug;
    if (sl && !slugLabel[sl]) { slugLabel[sl] = o.label; slugKind[sl] = o.kind; }
  }

  function renderPathTree(node: PathTree, depth: number, treeKey: string): React.ReactNode {
    const names = Object.keys(node.children).sort();
    return names.map(name => {
      const child = node.children[name];
      const isLeaf = Object.keys(child.children).length === 0;
      const label = slugLabel[name];
      const hoverKey = `${treeKey}/${name}@${depth}`;
      const showPopover = hoveredFolder === hoverKey && child.items.length > 0;
      const preview = child.items.slice(0, 12);
      return (
        <div key={name} style={{ paddingLeft: depth * 18 }}>
          <div
            style={{ display: 'flex', alignItems: 'baseline', gap: 6, padding: '2px 0', position: 'relative', cursor: child.items.length > 0 ? 'pointer' : 'default' }}
            onMouseEnter={() => setHoveredFolder(hoverKey)}
            onMouseLeave={() => setHoveredFolder(prev => (prev === hoverKey ? null : prev))}
            onClick={() => {
              if (child.items.length > 0) {
                setHoveredFolder(null);
                setFolderModal({ title: `${treeKey.split('/')[0]} · /${treeKey.split('/').slice(1).concat(name).join('/')}/`, isLeaf, count: child.count, items: child.items });
              }
            }}
            title={child.items.length > 0 ? 'Click to list the items' : undefined}
          >
            <span style={{ fontSize: 13 }}>{'\uD83D\uDCC1'}</span>
            <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#123250' }}>{name}</span>
            {label && label.toLowerCase() !== name.toLowerCase() && (
              <span style={{ fontSize: 11, color: '#8a94a0' }}>{label}</span>
            )}
            {/* Level badge (Fredrik 2026-08-14: 'other' read as a Type
                peer when it is the fallback TOPIC — the levels need to
                say what they are). Resolved per-slug, not per-depth,
                because nested topics occupy two depths. */}
            {(() => {
              const kind = /^\d{4}$/.test(name) ? 'year' : slugKind[name];
              return kind ? (
                <span style={{ fontSize: 9, color: '#adb5bd', textTransform: 'uppercase', letterSpacing: 0.5, border: '1px solid #e9ecef', borderRadius: 6, padding: '0 4px' }}>
                  {kind}
                </span>
              ) : null;
            })()}
            <span style={{
              fontSize: 10, color: isLeaf ? '#1e6b3a' : '#adb5bd',
              background: isLeaf ? '#e7f5ec' : 'transparent',
              borderRadius: 8, padding: isLeaf ? '0 6px' : 0
            }}>
              {child.count} item{child.count === 1 ? '' : 's'}
            </span>
            {showPopover && (
              <div style={{
                position: 'absolute', top: '100%', left: 24, zIndex: 20,
                background: '#fff', border: '1px solid #dee2e6', borderRadius: 8,
                boxShadow: '0 4px 14px rgba(18,50,80,0.14)', padding: '8px 12px',
                minWidth: 220, maxWidth: 340
              }}>
                <div style={{ fontSize: 10, color: '#8a94a0', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                  In this folder{isLeaf ? '' : ' (all levels below)'}
                </div>
                {preview.map((it, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#123250', padding: '1px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {it.name || '(untitled)'}
                    {it.version && <span style={{ color: '#8a94a0' }}> · V{it.version}</span>}
                  </div>
                ))}
                {child.count > preview.length && (
                  <div style={{ fontSize: 11, color: '#8a94a0', marginTop: 2 }}>
                    +{child.count - preview.length} more — click the folder for the full list
                  </div>
                )}
              </div>
            )}
          </div>
          {renderPathTree(child, depth + 1, `${treeKey}/${name}`)}
        </div>
      );
    });
  }

  function renderRows(kind: 'type' | 'channel' | 'audience' | 'topic' | 'geography') {
    // v2 — order parents before their children so the nesting reads.
    const flat = options.filter(o => o.kind === kind);
    const rootRows = flat.filter(o => !(o as any).parent);
    const rows: typeof flat = [];
    for (const r of rootRows) {
      rows.push(r);
      for (const c of flat.filter(o => (o as any).parent === r._id)) rows.push(c);
    }
    for (const o of flat) if (!rows.some(x => x._id === o._id)) rows.push(o);
    const nestable = kind === 'topic' || kind === 'audience';
    if (rows.length === 0) {
      return <p style={{ fontSize: 13, color: '#666' }}>No options yet — run the taxonomy seed.</p>;
    }
    return (
      <table className="registry-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
            <th style={{ padding: '6px 8px' }}>Label</th>
            {kind === 'channel' && <th style={{ padding: '6px 8px' }}>On approval</th>}
            {kind === 'channel' && <th style={{ padding: '6px 8px' }}>Accepted formats</th>}
            {kind === 'audience' && <th style={{ padding: '6px 8px' }}>HubSpot access</th>}
            {nestable && <th style={{ padding: '6px 8px' }}>Parent</th>}
            <th style={{ padding: '6px 8px' }}>Slug</th>
            <th style={{ padding: '6px 8px' }}>Origin</th>
            <th style={{ padding: '6px 8px' }}>Status</th>
            <th style={{ padding: '6px 8px' }} />
          </tr>
        </thead>
        <tbody>
          {rows.map(o => (
            <tr key={o._id} style={{ borderBottom: '1px solid #e9ecef', opacity: o.active ? 1 : 0.5 }}>
              <td style={{ padding: '6px 8px' }}>
                {editingId === o._id ? (
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <input value={editLabel} onChange={e => setEditLabel(e.target.value)} disabled={busy}
                      onKeyDown={e => { if (e.key === 'Enter') patch(o._id, { label: editLabel }); if (e.key === 'Escape') setEditingId(null); }} />
                    <button className="review-btn review-btn-primary" style={{ fontSize: 11, padding: '3px 8px' }}
                      onClick={() => patch(o._id, { label: editLabel })} disabled={busy}>Save</button>
                    <button className="review-btn review-btn-outline" style={{ fontSize: 11, padding: '3px 8px' }}
                      onClick={() => setEditingId(null)}>Cancel</button>
                    {usage && (
                      usage.labelStored && (usage.cycles > 0 || usage.archives > 0) ? (
                        <span style={{ fontSize: 11, color: '#a15c00', maxWidth: 340 }}>
                          {'⚠'} Used by {usage.cycles} item{usage.cycles === 1 ? '' : 's'}
                          {usage.archives > 0 ? ` (+${usage.archives} archived)` : ''} — they keep the OLD
                          label after this rename, and their lineage continues under it. Safe for new
                          content only, until rename-propagation ships.
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: '#6c757d', maxWidth: 340 }}>
                          {usage.cycles + usage.archives > 0
                            ? `Used by ${usage.cycles + usage.archives} item${usage.cycles + usage.archives === 1 ? '' : 's'} — `
                            : ''}display-only change: folders are named by the slug, so nothing moves and no URL changes.
                        </span>
                      )
                    )}
                  </span>
                ) : (
                  <strong>{(o as any).parent ? <span style={{ color: '#888' }}>{'\u2514 '}</span> : null}{o.label}</strong>
                )}
              </td>
              {nestable && (
                <td style={{ padding: '6px 8px' }}>
                  <select
                    value={(o as any).parent || ''}
                    onChange={e => patch(o._id, { parent: e.target.value || null })}
                    disabled={busy}
                    style={{ fontSize: 12, maxWidth: 180 }}
                    title="One level of nesting: e.g. per-indication topics under Indications, partner audiences under HCP."
                  >
                    <option value="">(top level)</option>
                    {flat.filter(p => p._id !== o._id && !(p as any).parent)
                      .map(p => <option key={p._id} value={p._id}>{p.label}</option>)}
                  </select>
                </td>
              )}
              <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11, color: '#6c757d' }}
                title="Immutable identity + folder segment. Renaming the label never changes it, which is why renames are safe.">
                {(o as any).slug || '\u2014'}
              </td>
              {kind === 'audience' && (
                <td style={{ padding: '6px 8px' }}>
                  <select
                    value={o.accessType || 'PUBLIC_NOT_INDEXABLE'}
                    onChange={e => patch(o._id, { accessType: e.target.value })}
                    disabled={busy}
                    style={{ fontSize: 12 }}
                  >
                    {Object.entries(ACCESS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {!o.seeded && o.accessType === 'PUBLIC_NOT_INDEXABLE' && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: '#c76d00' }}
                      title="User-added audience — safe-floor access until you confirm the mapping.">
                      default
                    </span>
                  )}
                </td>
              )}
              {kind === 'channel' && (
                <td style={{ padding: '6px 8px' }}>
                  <select
                    value={o.postApprovalAction || 'manual'}
                    onChange={e => patch(o._id, { postApprovalAction: e.target.value })}
                    disabled={busy}
                    style={{ fontSize: 12 }}
                  >
                    {Object.entries(ACTION_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {!o.seeded && o.postApprovalAction === 'manual' && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: '#c76d00' }}
                      title="User-added channel — routes to the notify flow until you map it to an automated handler.">
                      unmapped
                    </span>
                  )}
                </td>
              )}
              {kind === 'channel' && (
                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                  {ALL_FORMATS.map(f => {
                    const list = o.allowedFormats || [];
                    const allAllowed = list.length === 0;
                    const checked = allAllowed || list.includes(f);
                    return (
                      <label key={f} style={{ marginRight: 8, fontSize: 11, color: '#495057', cursor: 'pointer' }}
                        title={allAllowed ? 'Empty list = all formats allowed. Unchecking one pins the list.' : undefined}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={busy}
                          onChange={() => {
                            const base = allAllowed ? [...ALL_FORMATS] : [...list];
                            const next = base.includes(f) ? base.filter(x => x !== f) : [...base, f];
                            // Walk fix F7 (2026-08-12) — an empty list means
                            // "all formats allowed" on the backend, so letting
                            // the last checkbox clear would silently INVERT the
                            // restriction (and re-render fully checked). Block
                            // it; "accepts nothing" isn't expressible — that's
                            // what deactivating the channel is for.
                            if (next.length === 0) {
                              setError(`At least one format must stay allowed for ${o.label} — an empty list means ALL formats. Deactivate the channel instead if it should accept nothing.`);
                              return;
                            }
                            setError(null);
                            patch(o._id, { allowedFormats: next });
                          }}
                          style={{ marginRight: 2 }}
                        />
                        {f}
                      </label>
                    );
                  })}
                  {allFormatsBadge(o)}
                </td>
              )}
              <td style={{ padding: '6px 8px', fontSize: 12, color: '#666' }}>
                {o.seeded ? 'default' : `added by ${o.addedBy?.name || 'unknown'}`}
              </td>
              <td style={{ padding: '6px 8px', fontSize: 12 }}>
                {o.active
                  ? <span style={{ color: '#1e6b3a' }}>active</span>
                  : <span style={{ color: '#888' }}>inactive</span>}
              </td>
              <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                <button className="review-btn review-btn-outline" style={{ fontSize: 11, padding: '3px 8px', marginRight: 6 }}
                  onClick={() => { setEditingId(o._id); setEditLabel(o.label); fetchUsage(o._id); }}>
                  Rename
                </button>
                <button className="review-btn review-btn-outline" style={{ fontSize: 11, padding: '3px 8px' }}
                  onClick={() => patch(o._id, { active: !o.active })} disabled={busy}>
                  {o.active ? 'Deactivate' : 'Reactivate'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    // Walk sidenote (Fredrik 2026-08-13): the registries' selects/inputs/
    // checkboxes were browser-default — the taxonomy-admin class scopes the
    // shared control styling in ContentReview.css so they match the site.
    <div className="taxonomy-admin">
      {error && <div style={{ color: '#dc3545', padding: 12, background: '#f8d7da', borderRadius: 8, marginBottom: 16 }}>{error}</div>}
      <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
        Content taxonomy: <strong>Type</strong> describes what the content is (drives folders + filenames);{' '}
        <strong>Channel</strong> decides what happens after approval;{' '}
        <strong>Audience</strong> maps to the HubSpot file access applied where files are uploaded to HubSpot;{' '}
        <strong>Topic</strong> mirrors the portal navigation categories (nest per-indication topics under Indications);{' '}
        <strong>Geography</strong> is Global plus the three hubs. Folder paths compose from the slugs automatically —
        relabelling anything here never moves a folder. Deactivating hides an option from dropdowns without touching history.
      </p>
      {([
        ['Channels', 'channel'],
        ['Types', 'type'],
        ['Audiences', 'audience'],
        ['Topics', 'topic'],
        ['Geographies', 'geography']
      ] as Array<[string, 'channel' | 'type' | 'audience' | 'topic' | 'geography']>).map(([title, kind], i) => (
        <div key={kind}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: i === 0 ? '0 0 8px' : '20px 0 8px' }}>
            <h3 style={{ fontSize: 16, color: '#123250', margin: 0 }}>{title}</h3>
            {addingKind === kind ? (
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <input
                  autoFocus
                  value={addLabel}
                  onChange={e => setAddLabel(e.target.value)}
                  placeholder={`New ${kind}...`}
                  disabled={busy}
                  onKeyDown={e => { if (e.key === 'Enter') addOption(kind); if (e.key === 'Escape') { setAddingKind(null); setAddLabel(''); setAddParent(''); } }}
                  style={{ fontSize: 12 }}
                />
                {(kind === 'topic' || kind === 'audience') && (
                  <select value={addParent} onChange={e => setAddParent(e.target.value)} disabled={busy} style={{ fontSize: 12 }}>
                    <option value="">(top level)</option>
                    {options.filter(o => o.kind === kind && !(o as any).parent && o.active)
                      .map(o => <option key={o._id} value={(o as any).slug || o._id}>{'under ' + o.label}</option>)}
                  </select>
                )}
                <button className="review-btn review-btn-primary" style={{ fontSize: 11, padding: '3px 8px' }}
                  onClick={() => addOption(kind)} disabled={busy}>Add</button>
                <button className="review-btn review-btn-outline" style={{ fontSize: 11, padding: '3px 8px' }}
                  onClick={() => { setAddingKind(null); setAddLabel(''); setAddParent(''); }}>Cancel</button>
              </span>
            ) : (
              <button className="review-btn review-btn-outline" style={{ fontSize: 11, padding: '3px 8px' }}
                onClick={() => { setAddingKind(kind); setAddLabel(''); setAddParent(''); }}>
                + Add
              </button>
            )}
          </div>
          {renderRows(kind)}
        </div>
      ))}

      {/* Fredrik 2026-08-14 — the live folder overview. Structure is not
          managed anywhere: it COMPOSES from each item's dimensions, so
          this renders the folders that actually exist (approved content),
          slug-named exactly as they appear in SharePoint/HubSpot. */}
      <h3 style={{ fontSize: 16, color: '#123250', margin: '24px 0 4px' }}>Folder structure (live)</h3>
      <p style={{ fontSize: 12, color: '#666', margin: '0 0 10px' }}>
        Composed automatically from each approved item's Channel / Geography / Topic / Type — there is
        nothing to manage here. Folders are named by slugs, so renaming a label above never moves a
        folder or changes a URL. To restructure: edit the registries (future content) or an item's
        dimensions before its decision.
      </p>
      {!structure || (structure.approved.length + structure.archive.length + structure.working.length === 0) ? (
        <p style={{ fontSize: 12, color: '#888', fontStyle: 'italic' }}>
          No approved content yet — folders appear here as approvals create them.
        </p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {([
            ['HubSpot — Approved content', 'approved', '/Approved content/'],
            ['SharePoint — Archive', 'archive', ''],
            ['SharePoint — Working files', 'working', '']
          ] as Array<[string, 'approved' | 'archive' | 'working', string]>).map(([title, key, prefix]) => (
            <div key={key} style={{ background: '#fafbfc', border: '1px solid #e9ecef', borderRadius: 8, padding: 12 }}>
              <strong style={{ fontSize: 13, color: '#123250' }}>{title}</strong>
              {prefix && (
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#8a94a0', marginTop: 6 }}>{prefix}</div>
              )}
              <div style={{ marginTop: 6 }}>
                {renderPathTree(buildPathTree(structure[key]), 0, key)}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Folder contents popup — full scrollable list; items click through
          to their review cycle. */}
      {folderModal && (
        <div className="review-modal-overlay" onClick={() => setFolderModal(null)}>
          <div className="review-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <h3 style={{ color: '#1a1a1a', marginTop: 0, fontFamily: 'monospace', fontSize: 15 }}>
              {'\uD83D\uDCC1'} {folderModal.title}
            </h3>
            <p style={{ fontSize: 12, color: '#666', margin: '0 0 10px' }}>
              {folderModal.count} item{folderModal.count === 1 ? '' : 's'}
              {folderModal.isLeaf ? '' : ' across all folders below'}
              {onOpenCycle ? ' — click one to open its review.' : '.'}
            </p>
            <div style={{ maxHeight: '55vh', overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: 8 }}>
              {folderModal.items.map((it, i) => (
                <div
                  key={it._id || i}
                  onClick={() => { if (onOpenCycle && it._id) { setFolderModal(null); onOpenCycle(it._id); } }}
                  onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && onOpenCycle && it._id) { e.preventDefault(); setFolderModal(null); onOpenCycle(it._id); } }}
                  role={onOpenCycle && it._id ? 'button' : undefined}
                  tabIndex={onOpenCycle && it._id ? 0 : undefined}
                  style={{
                    padding: '8px 12px',
                    borderBottom: i < folderModal.items.length - 1 ? '1px solid #f1f3f5' : 'none',
                    cursor: onOpenCycle && it._id ? 'pointer' : 'default',
                    fontSize: 13, color: '#123250',
                    display: 'flex', justifyContent: 'space-between', gap: 12
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f6f9fc'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.name || '(untitled)'}
                  </span>
                  {it.version && <span style={{ color: '#8a94a0', flexShrink: 0 }}>V{it.version}</span>}
                </div>
              ))}
            </div>
            {folderModal.count > folderModal.items.length && (
              <p style={{ fontSize: 11, color: '#8a94a0', margin: '8px 0 0' }}>
                Showing the first {folderModal.items.length} of {folderModal.count} — the rest are findable in Archive search.
              </p>
            )}
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <button className="review-btn review-btn-outline" style={{ fontSize: 12, padding: '5px 12px' }}
                onClick={() => setFolderModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxonomyAdmin;
